import React from 'react';
import { FaIndustry, FaCertificate, FaUsers, FaGlobe } from 'react-icons/fa';

const AboutPage = () => {
  const milestones = [
    { year: '2020', title: 'Company Founded', description: 'Shivangel Technopack established' },
    { year: '2021', title: 'Ayurvedic Division', description: 'Launched Ayurvedic manufacturing' },
    { year: '2022', title: 'GMP Certification', description: 'Achieved GMP & ISO certification' },
    { year: '2024', title: 'Facility Expansion', description: 'Expanded to 25,000+ sq ft' },
    { year: '2025', title: 'Industry Leader', description: 'Serving 100+ brands nationwide' },
  ];

  return (
    <div className="min-h-screen" data-testid="about-page">
      {/* Hero */}
      <section className="bg-gradient-to-r from-[#0F5132] to-[#0F5132] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">About Shivangel</h1>
          <p className="text-xl text-gray-100 max-w-3xl mx-auto">
            Pioneering excellence in Ayurvedic and Nutraceutical manufacturing since 2020
          </p>
        </div>
      </section>

      {/* Company Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#0F5132] mb-6">
                A Legacy of Quality & Trust
              </h2>
              <div className="space-y-4 text-[#0F5132] leading-relaxed">
                <p>
                  <strong>Shivangel Ayurveda and Nutraceuticals</strong> is a unit of Shivangel Technopack Pvt. Ltd., 
                  established with a vision to bridge traditional Ayurvedic wisdom with modern manufacturing excellence.
                </p>
                <p>
                  Our state-of-the-art 25,000+ sq ft manufacturing facility in Panchkula, Haryana, is equipped with 
                  automated production lines and adheres to the highest quality standards including ISO, GMP, and AYUSH 
                  certifications.
                </p>
                <p>
                  We specialize in third-party manufacturing, helping entrepreneurs and established brands launch 
                  high-quality Ayurvedic and Nutraceutical products with complete regulatory compliance and end-to-end support.
                </p>
              </div>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1582719471137-c3967ffb1c42?w=800&q=80"
                alt="Manufacturing Facility"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { icon: FaIndustry, number: '25,000+', label: 'Sq Ft Facility' },
              { icon: FaCertificate, number: '7+', label: 'Major Certifications' },
              { icon: FaUsers, number: '100+', label: 'Brand Partners' },
              { icon: FaGlobe, number: '140+', label: 'Product Range' },
            ].map((stat, index) => (
              <div key={index} className="text-center bg-white rounded-xl p-6 shadow-lg">
                <stat.icon className="text-4xl text-[#0F5132] mx-auto mb-4" />
                <div className="text-3xl font-bold text-[#0F5132] mb-2">{stat.number}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0F5132] mb-4">Our Journey</h2>
            <p className="text-lg text-gray-600">Milestones that shaped our success</p>
          </div>

          <div className="relative">
            <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-[#0F5132]/20 transform -translate-x-1/2"></div>

            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <div
                  key={index}
                  className={`flex flex-col md:flex-row items-center gap-8 ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                    <div className="bg-gray-50 rounded-xl p-6 inline-block shadow-md">
                      <div className="text-[#0F5132] font-bold text-xl mb-2">{milestone.year}</div>
                      <h3 className="text-xl font-bold text-[#0F5132] mb-2">{milestone.title}</h3>
                      <p className="text-gray-600">{milestone.description}</p>
                    </div>
                  </div>

                  <div className="relative z-10">
                    <div className="w-16 h-16 bg-[#0F5132] rounded-full flex items-center justify-center shadow-xl">
                      <div className="w-8 h-8 bg-[#0F5132] rounded-full"></div>
                    </div>
                  </div>

                  <div className="flex-1 hidden md:block"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h3 className="text-2xl font-bold text-[#0F5132] mb-4">Our Mission</h3>
              <p className="text-[#0F5132] leading-relaxed">
                To empower entrepreneurs and brands by providing world-class Ayurvedic and Nutraceutical 
                manufacturing services, combining traditional wisdom with modern technology to deliver products 
                that enhance health and wellness.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h3 className="text-2xl font-bold text-[#0F5132] mb-4">Our Vision</h3>
              <p className="text-[#0F5132] leading-relaxed">
                To become India's most trusted third-party manufacturing partner in the Ayurvedic and 
                Nutraceutical sector, known for quality, innovation, and customer success, while making 
                wellness accessible to millions.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
